import React, { useState } from 'react';
import { X, Users, Clock } from 'lucide-react';
import { Meeting } from '../../../types/meeting';

interface MeetingSchedulerProps {
  onSchedule: (meeting: Omit<Meeting, 'id'>) => void;
  onClose: () => void;
}

export default function MeetingScheduler({ onSchedule, onClose }: MeetingSchedulerProps) {
  const [formData, setFormData] = useState({
    topic: '',
    description: '',
    startTime: '',
    duration: '60',
    participants: [] as string[]
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSchedule({
      topic: formData.topic,
      description: formData.description,
      startTime: new Date(formData.startTime).toISOString(),
      duration: parseInt(formData.duration),
      participants: formData.participants.length
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Schedule Meeting</h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Topic</label>
            <input
              type="text"
              value={formData.topic}
              onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Date & Time</label>
              <input
                type="datetime-local"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Duration (minutes)</label>
              <select
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="90">1.5 hours</option>
                <option value="120">2 hours</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Participants</label>
            <div className="mt-1 flex items-center space-x-2">
              <input
                type="email"
                placeholder="Enter email and press Enter"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const email = e.currentTarget.value.trim();
                    if (email && !formData.participants.includes(email)) {
                      setFormData({
                        ...formData,
                        participants: [...formData.participants, email]
                      });
                      e.currentTarget.value = '';
                    }
                  }
                }}
              />
            </div>
            {formData.participants.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.participants.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        participants: formData.participants.filter(p => p !== email)
                      })}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              Schedule Meeting
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}